import { GuiTextbox } from "../components/global-ui/gui-textbox";
import "../styles/edit-profile.scss";
import { useUpdateState } from "../utilities/utilities";
import { GuiButton } from "../components/global-ui/gui-button";
import user from "../actions/user-actions";
import { useEffect, useState } from "react";
import CircularProgress from "../actions/CircularProgress";
import { showToast } from "../components/toast";
import { UserRoleName, UserRole } from "../utilities/constants";
import { GuiSelect } from "../components/global-ui/gui-select";
import { FileUploadButton } from "../components/image-library-modal/file-upload";
import api from "../api/api";
import { appState } from "../redux/redux-config";
import CropperModal from "./CropperModal";

export default function EditProfile({
  userId,
  displayModal,
  onCloseClick,
  onSuccessfulAdd,
  loginUserType,
}) {
  let [errors, setErrors] = useState({
    email: "",
    firstName: "",
    lastName: "",
    username: "",
    uniqueId: "",
  });
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const usernameRegex = /^[A-Za-z0-9._-]+$/;
  const uniqueIdRegex = /^[A-Za-z0-9._-]+$/;
  const currentLoginUserType = loginUserType ?? appState().user.userType;
  const userIsAdmin = currentLoginUserType === UserRole.Admin;
  const isLevel1User = currentLoginUserType === UserRole.Member; // Member = Student = type 1
  const isLevel2User = currentLoginUserType === UserRole.Staff; // Staff (= 3) = Level 2 Faculty in backend
  const onSaveSuccess = onSuccessfulAdd || (() => {});
  let [state, updateState] = useUpdateState({
    firstName: "",
    lastName: "",
    username: "",
    uniqueId: "",
    userType: "",
    loading: true,
    primaryEmail: "",
    isUpdateDisabled: false,
    userTypeName: "",
    profilePhotoURL: "",
    fileUrl: "",
    userTypeOptions: [],
    currentUserType: "",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    getUserDetails();
  }, []);

  async function getUserDetails() {
    let state = appState().user;
    updateState({ loading: true });
    if (userId) {
      await user
        .getSpecificDetailForSpecificUser(userId)
        .then(async (result) => {
          let response = await api.user.getUserTypes(state.token);

          if (result.success && response.success) {
            const userTypeName = Object.keys(UserRoleName).find(
              (role) => UserRoleName[role] === result.userType,
            );
            const username = result.username || result.userName || "";
            const uniqueId = result.uniqueId || result.UniqueId || "";

            updateState({
              firstName: result.firstName,
              lastName: result.lastName,
              username: username,
              uniqueId: uniqueId,
              userType: result.userType,
              currentUserType: result.userType,
              primaryEmail: result.emailAddress,
              userTypeOptions: response.list.map((g) => ({
                value: g.id,
                label: g.name,
              })),
              userTypeName: userTypeName,
              loading: false,
              profilePhotoURL: result.profilePhotoURL,
            });
          }
        });
      return;
    } else {
      await user.getSpecificUserDetails().then(async (result) => {
        let response = await api.user.getUserTypes(state.token);

        if (result.success && response.success) {
          const userTypeName = Object.keys(UserRoleName).find(
            (role) => UserRoleName[role] === result.userType,
          );
          const username = result.username || result.userName || "";
          const uniqueId = result.uniqueId || result.UniqueId || "";

          updateState({
            firstName: result.firstName,
            lastName: result.lastName,
            username: username,
            uniqueId: uniqueId,
            userType: result.userType,
            currentUserType: result.userType,
            primaryEmail: result.emailAddress,
            userTypeOptions: response.list.map((g) => ({
              value: g.id,
              label: g.name,
            })),
            userTypeName: userTypeName,
            loading: false,
            profilePhotoURL: result.profilePhotoURL,
          });
        }
      });
    }
  }
  const handleChange = (name, value) => {
    updateState(name, value);

    if (name === "username") {
      setErrors((prev) => ({
        ...prev,
        username:
          userIsAdmin || isLevel1User || isLevel2User
            ? validateUsername(value)
            : "",
      }));
    }

    if (name === "uniqueId") {
      setErrors((prev) => ({
        ...prev,
        uniqueId: userIsAdmin ? validateUniqueId(value) : "",
      }));
    }
  };

  function validateUsername(input) {
    const value = (input || "").trim();
    if (!value) return "Username is required";
    if (value.length > 50) return "Username max 50 characters are allowed";
    if (!usernameRegex.test(value)) {
      return "Username can only contain letters, numbers, periods, underscores, and hyphens";
    }
    return "";
  }

  function validateUniqueId(input) {
    const value = (input || "").trim();
    if (!value) return "";
    if (value.length > 50) return "Unique ID max 50 characters are allowed";
    if (!uniqueIdRegex.test(value)) {
      return "Unique ID can only contain letters, numbers, periods, underscores, and hyphens";
    }
    return "";
  }

  async function onUpdateClick() {
    if (!validateFields()) {
      return;
    }

    updateState({ loading: true, isUpdateDisabled: true });

    const payload = {
      firstName: state.firstName,
      lastName: state.lastName,
      emailAddress: state.primaryEmail,
      userName: state.username?.trim(),
      uniqueId: state.uniqueId?.trim() || null,
      UserTypeId: state.userType,
    };
    if (userId) {
      let response = await user.updateUserProfileForSpecificUser(
        payload,
        userId,
      );
      if (response?.success) {
        if (state.fileUrl !== "") {
          await user.uploadProfilePhotoForSpecificUser(state.fileUrl, userId);
        }
        updateState({ loading: false });
        onSaveSuccess();
        showToast("success", "Profile info Updated");
      }
    } else {
      let response = await user.updateUserProfile(payload);
      if (response?.success) {
        if (state.fileUrl !== "") {
          await user.uploadProfilePhoto(state.fileUrl);
        }
        updateState({ loading: false });
        onSaveSuccess();
        showToast("success", "Profile info Updated");
      }
    }
    onCloseClick();
  }
  async function handlefileChange(file) {
    updateState({ fileUrl: file });
    let imagePreview = URL.createObjectURL(file);
    updateState({ imgSrc: imagePreview });
    setModalOpen(true);
  }

  const validateFields = () => {
    let hasError = false;
    let newErrors = {
      email: "",
      firstName: "",
      lastName: "",
      username: "",
      uniqueId: "",
    };

    // First Name
    if (!state.firstName.trim()) {
      newErrors.firstName = "First name is required";
      hasError = true;
    } else if (state.firstName.trim().length > 100) {
      newErrors.firstName = "First name max 100 characters are allowed";
      hasError = true;
    }

    // Last Name
    if (!state.lastName.trim()) {
      newErrors.lastName = "Last name is required";
      hasError = true;
    } else if (state.lastName.trim().length > 100) {
      newErrors.lastName = "Last name max 100 characters are allowed";
      hasError = true;
    }

    // Email
    if (state.primaryEmail == undefined || !state.primaryEmail.trim()) {
      newErrors.email = "Email is required";
      hasError = true;
    } else if (state.primaryEmail.trim().length > 100) {
      newErrors.email = "Email max 100 characters are allowed";
      hasError = true;
    } else {
      const result = validateEmail(state.primaryEmail.trim());
      if (!result.isValid) {
        newErrors.email = result.message;
        hasError = true;
      }
    }

    if (userIsAdmin || isLevel1User || isLevel2User) {
      newErrors.username = validateUsername(state.username);
      if (newErrors.username) {
        hasError = true;
      }
    }

    if (userIsAdmin) {
      newErrors.uniqueId = validateUniqueId(state.uniqueId);
      if (newErrors.uniqueId) {
        hasError = true;
      }
    }

    setErrors(newErrors);
    return !hasError;
  };

  function validateEmail(input) {
    if (!input) {
      return { isValid: false, message: "Email is required" };
    }
    if (emailRegex.test(input)) {
      return { isValid: true };
    } else {
      return { isValid: false, message: "Invalid Email format" };
    }
  }

  return (
    <>
      <div className={`edit-profile Modal ${displayModal ? "Show" : "hide"}`}>
        {state?.loading ? (
          <CircularProgress />
        ) : (
          <>
            <div className="edit-profile-header">
              <h4 className="edit-profile-title">Edit Profile</h4>
              <span onClick={onCloseClick}>
                <i className="fa fa-close close-primary-icon cursor-pointer"></i>
              </span>
            </div>
            <div className="edit-profile-body">
              <div>
                <span className="edit-profile-body-title">Profile Photo</span>
                <div className="edit-profile-body-image">
                  <div className="background-curve"></div>
                  <div className="profile-image-container">
                    <div className="edit-icon">
                      <span
                        style={
                          !userIsAdmin && !isLevel1User && !isLevel2User
                            ? {
                                pointerEvents: "none",
                                opacity: 0.6,
                                cursor: "not-allowed",
                              }
                            : {}
                        }
                      >
                        <FileUploadButton
                          name="logoUrl"
                          isUploadIcon="true"
                          onChange={(file) => handlefileChange(file)}
                          acceptedTypes=".jpg, .jpeg, .png"
                          maxFileSizeMB={5}
                          disabled={
                            !userIsAdmin && !isLevel1User && !isLevel2User
                          }
                        />
                      </span>
                    </div>
                    <CropperModal
                      modalOpen={modalOpen}
                      src={state?.imgSrc}
                      setPreview={(item) => updateState({ imgSrc: item })}
                      setModalOpen={setModalOpen}
                      profilePhotoURL={state?.profilePhotoURL}
                      setFormData={(file) => updateState({ fileUrl: file })}
                    />
                    {
                      // Show initials only if no image source or image hasn't loaded
                      (!state?.imgSrc && !state?.profilePhotoURL) ||
                      !imageLoaded ? (
                        <div className="initials">
                          {(state?.firstName + " " + state?.lastName)
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </div>
                      ) : null
                    }

                    {(state?.imgSrc || state?.profilePhotoURL) && (
                      <img
                        src={
                          state?.imgSrc && !modalOpen
                            ? state?.imgSrc
                            : state?.profilePhotoURL
                        }
                        alt="Profile"
                        className={`profile-image ${
                          imageLoaded ? "visible" : "hidden"
                        }`}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageLoaded(false)}
                      />
                    )}
                  </div>
                </div>
                <div className="edit-profile-body-textbox">
                  <GuiTextbox
                    label="First Name"
                    name="firstName"
                    value={state?.firstName}
                    onChange={(name, value) => handleChange(name, value)}
                    disabled={!userIsAdmin && !isLevel1User && !isLevel2User}
                    maxLength={100}
                  />
                  {errors?.firstName && (
                    <div className="error-class">{errors.firstName}</div>
                  )}
                </div>
                <div className="edit-profile-body-textbox">
                  <GuiTextbox
                    label="Last Name"
                    name="lastName"
                    value={state?.lastName}
                    onChange={(name, value) => handleChange(name, value)}
                    disabled={!userIsAdmin && !isLevel1User && !isLevel2User}
                    maxLength={100}
                  />
                  {errors?.lastName && (
                    <div className="error-class">{errors.lastName}</div>
                  )}
                </div>
                <div className="edit-profile-body-select">
                  {state?.currentUserType !== 4 ? (
                    <GuiSelect
                      label="User Type"
                      name="userType"
                      value={state?.userType}
                      options={state?.userTypeOptions}
                      disabled={isLevel1User || isLevel2User}
                      onChange={(name, option) =>
                        handleChange(name, option.value)
                      }
                    />
                  ) : (
                    <div>
                      <span className="user-type-name">User Type : </span>
                      <GuiButton
                        className={`role-badge${
                          state?.userType === 1 ? "-student" : "-admin"
                        }`}
                      >
                        {state?.userTypeName}
                      </GuiButton>
                    </div>
                  )}
                </div>
                <div className="edit-profile-body-textbox">
                  <GuiTextbox
                    label="Username"
                    name="username"
                    value={state?.username}
                    onChange={(name, value) => handleChange(name, value)}
                    disabled={!userIsAdmin && !isLevel1User && !isLevel2User}
                    maxLength={50}
                  />
                  {errors?.username && (
                    <div className="error-class">{errors?.username}</div>
                  )}
                </div>
                <div className="edit-profile-body-textbox">
                  <GuiTextbox
                    label="Unique ID"
                    name="uniqueId"
                    value={state?.uniqueId}
                    onChange={(name, value) => handleChange(name, value)}
                    disabled={!userIsAdmin}
                    maxLength={50}
                  />
                  {errors?.uniqueId && (
                    <div className="error-class">{errors?.uniqueId}</div>
                  )}
                </div>
                <h4 className="section-heading">Primary Contact</h4>
                <div className="edit-profile-body-textbox">
                  <GuiTextbox
                    label="Primary Email"
                    name="primaryEmail"
                    value={state?.primaryEmail}
                    disabled={!userIsAdmin && !isLevel1User && !isLevel2User}
                    onChange={(name, value) => handleChange(name, value)}
                    maxLength={100}
                  />
                  {errors?.email && (
                    <div className="error-class">{errors?.email}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="hr-divider"></div>

            <div className="edit-profile-btns">
              <GuiButton className="cancel-btn" onClick={onCloseClick}>
                Cancel
              </GuiButton>
              {(userIsAdmin || isLevel1User || isLevel2User) && (
                <GuiButton
                  className="update-btn"
                  disabled={state?.isUpdateDisabled}
                  onClick={onUpdateClick}
                >
                  Update
                </GuiButton>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
